import { useCallback, useEffect, useRef, useState } from 'react';
import { HiChevronDown as ChevronDownIcon } from 'react-icons/hi2';
import { autoUpdate, flip, size, useFloating } from '@floating-ui/react';
import { Button, Checkbox, Chip, Divider } from '@knack/asterisk-react';
import { t } from 'i18next';

import { cn } from '@/utils/tailwind';
import { CellErrors } from '@/components/data-table/display/fields/CellErrors';
import { type MultipleChoiceField } from '@/components/data-table/display/fields/Field';
import { type FieldRenderProps } from '@/components/data-table/display/fields/FieldRender';
import { TextareaCell } from '@/components/data-table/display/TextareaCell';
import { useSelectionStrategy } from '@/components/data-table/display/useSelectionStrategy';
import { setCursorPositionAtTheEnd } from '@/components/data-table/helpers/setCursorPositionAtTheEnd';
import { useDataTableStore } from '@/components/data-table/useDataTableStore';

export function MultipleChoiceEdit({
  fieldId,
  value,
  rawValue,
  rowId,
  type
}: FieldRenderProps<MultipleChoiceField>) {
  const currentField = useDataTableStore().use.getField<typeof type>(fieldId);
  const selectedCell = useDataTableStore().use.selectedCell();
  const cellErrors = useDataTableStore().use.cellErrors(rowId, fieldId);
  const { saveCell, setIsEditing, clearCellErrors } = useDataTableStore().use.actions();

  if (!selectedCell) throw new Error('No selected cell');

  const [currentValue, setCurrentValue] = useState(value);
  const [currentRawValue, setCurrentRawValue] = useState(rawValue);
  const [selectAllOptions, setSelectAllOptions] = useState(false);

  const selectedOptionRef = useRef<HTMLButtonElement>(null);
  const selectContainerRef = useRef<HTMLDivElement>(null);

  const { moveSelectionHorizontal } = useSelectionStrategy();
  const [searchKeyword, setSearchKeyword] = useState('');

  const { options = [], type: formatType } = currentField.format;
  const isMultiselect = formatType === 'multi' || formatType === 'checkboxes';
  const filteredOptions = options?.filter((option) =>
    option.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    middleware: [
      // Fill the available height for big lists
      size({
        padding: 500,
        apply({ availableHeight, elements }) {
          if (parseInt(elements.floating.style.height, 10) < availableHeight) {
            elements.floating.style.height = `${availableHeight}px`;
          }
        }
      }),
      flip({
        crossAxis: true
      })
    ],
    whileElementsMounted: autoUpdate
  });

  const saveValue = useCallback(
    (option: string | string[]) => {
      void saveCell(rowId, fieldId, option, {
        value: typeof option === 'string' ? option : option.join(', '),
        rawValue: option
      });
    },
    [saveCell, rowId, fieldId]
  );

  const onSelection = async (option: string) => {
    setCurrentValue(option);

    if (option === currentValue) {
      setIsEditing(false);
      return;
    }

    saveValue(option);

    setCurrentValue(option);
    setCurrentRawValue(option);

    (refs.reference.current as HTMLTextAreaElement).value = option;
  };

  const onMultiSelection = async (option: string) => {
    if (typeof currentRawValue === 'string') return;
    let multiSelectValues = currentRawValue;
    if (currentRawValue.includes(option)) {
      multiSelectValues = multiSelectValues.filter((checkedOption) => checkedOption !== option);
    } else {
      multiSelectValues = [...multiSelectValues, option];
    }

    saveValue(multiSelectValues);

    setCurrentValue(multiSelectValues.join(', '));
    setCurrentRawValue(multiSelectValues);
  };

  const handleSelectAll = () => {
    setSelectAllOptions(!selectAllOptions);

    let multiSelectValues = currentRawValue as string[];
    if (selectAllOptions) {
      multiSelectValues = [];
    } else {
      multiSelectValues = options;
    }

    saveValue(multiSelectValues);

    setCurrentValue(multiSelectValues.join(', '));
    setCurrentRawValue(multiSelectValues);
  };

  const getCurrentOptionIndex = () => {
    // Active element refers to the focused element
    const child = document.activeElement;
    const parent = selectContainerRef.current;

    let currentOptionIndex = -1; // Default to -1 if not found
    if (parent && child) {
      // Convert HTMLCollection to Array
      const childrenArray = Array.from(parent?.children);
      currentOptionIndex = childrenArray.indexOf(child?.parentNode as HTMLDivElement);
    }
    return currentOptionIndex;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLButtonElement>) => {
    if (e.key === 'Escape') {
      setIsEditing(false);
      e.preventDefault();
    }

    if (selectedCell.isEditing) {
      if (e.key === 'ArrowUp') {
        const optionsList = selectContainerRef.current?.children;
        if (!optionsList || optionsList.length === 0) return;

        const currentOptionIndex = getCurrentOptionIndex();

        let nextIndex = currentOptionIndex - 1;
        if (nextIndex < 0) nextIndex = optionsList.length - 1;

        // As in multiselect we use Radix checkbox and has a button inside
        // The proper button we are looking to focus is the second child
        const buttonIndex = isMultiselect ? 1 : 0;
        selectContainerRef.current?.children[nextIndex]
          ?.getElementsByTagName('button')
          [buttonIndex]?.focus();

        e.preventDefault();
        e.stopPropagation();
      }

      if (e.key === 'ArrowDown') {
        const optionsList = selectContainerRef.current?.children;
        if (!optionsList || optionsList.length === 0) return;

        const currentOptionIndex = getCurrentOptionIndex();

        let nextIndex = currentOptionIndex + 1;
        if (nextIndex > optionsList.length - 1) nextIndex = 0;

        // As in multiselect we use Radix checkbox and has a button inside
        // The proper button we are looking to focus is the second child
        const buttonIndex = isMultiselect ? 1 : 0;
        selectContainerRef.current?.children[nextIndex]
          ?.getElementsByTagName('button')
          [buttonIndex].focus();

        e.preventDefault();
        e.stopPropagation();
      }
    }

    if (e.key === 'Tab' && selectedCell.isEditing) {
      moveSelectionHorizontal('right');
      setIsEditing(false);
      e.stopPropagation();
      e.preventDefault();
    }
  };

  useEffect(() => {
    // Hack needed to focus the textarea when the component is created
    setTimeout(() => {
      (refs.reference.current as HTMLTextAreaElement).focus();
    }, 0);
  }, [refs.reference]);

  // The selected option gets the focus when it changes or when you start editing
  // This way if you have a scrollbar the element will be scrolled into view
  useEffect(() => {
    if (selectedCell.isEditing) {
      clearCellErrors(rowId, fieldId);

      selectedOptionRef.current?.focus();
    }
  }, [selectedCell.isEditing, currentValue, clearCellErrors, rowId, fieldId]);

  return (
    <div ref={refs.setReference} className="flex size-full">
      <div
        className={cn('flex size-full flex-col border-none bg-base', {
          'opacity-0': !selectedCell.isEditing,
          'ring-2 ring-destructive focus:ring-2 focus:ring-destructive': cellErrors
        })}
      >
        {isMultiselect && selectedCell.isEditing && currentRawValue.length > 0 && (
          <div className="mb-1 h-3/5 overflow-y-auto p-0.5">
            {(currentRawValue as string[])?.map((option) => (
              <Chip
                className="mx-0.5 mb-1 mt-0 p-2 text-sm font-medium"
                text={option}
                data-testid={`multiple-choice-multi-selected-tag-${option}-${fieldId}-${rowId}`}
                onClick={() => onMultiSelection(option)}
                key={option}
              />
            ))}
          </div>
        )}

        <TextareaCell
          defaultValue={isMultiselect ? '' : currentValue}
          intent={cellErrors ? 'destructive' : 'default'}
          className={cn('min-h-2/5 ml-0.5 flex-grow resize-none p-0 ring-0 focus:ring-0', {
            'opacity-0': !selectedCell.isEditing
          })}
          onKeyDown={handleKeyDown}
          onChange={(e) => {
            setIsEditing(true);
            setSearchKeyword(e.target.value);
          }}
          onClick={(e) => {
            if (!selectedCell.isEditing) {
              setIsEditing(true);
              setCursorPositionAtTheEnd(e);
            }
          }}
          data-testid={`edit-multiple-choice-input-${rowId}-${fieldId}`}
          onFocus={setCursorPositionAtTheEnd}
        />
      </div>

      {selectedCell.isEditing && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className="mt-1 flex max-h-[400px] w-full min-w-[300px] flex-col items-start overflow-auto rounded-lg border-2 border-default bg-base p-2"
        >
          {searchKeyword !== '' && filteredOptions?.length === 0 ? (
            <div className="p-2">{t('components.data_table.no_matching_options')}</div>
          ) : (
            <>
              {isMultiselect && (
                <>
                  <div className="my-2 flex w-full justify-between">
                    <div className="flex items-center">
                      <Checkbox
                        data-testid={`multiple-choice-field-selectAll-checkbox-${fieldId}-${rowId}`}
                        className="mr-4"
                        checked={selectAllOptions || currentRawValue.length === options.length}
                        onCheckedChange={handleSelectAll}
                      />
                      <span className="font-normal">{t('components.data_table.select_all')}</span>
                    </div>
                    <div>
                      <span
                        data-testid={`multiple-choice-field-selectAll-counter-${fieldId}-${rowId}`}
                      >
                        {t('components.data_table.options_selected', {
                          selected_options: currentRawValue.length
                        })}
                      </span>
                    </div>
                  </div>
                  <Divider className="mb-1" />
                </>
              )}

              <div className="w-full" ref={selectContainerRef}>
                {!isMultiselect && !searchKeyword && (
                  <div>
                    <Button
                      intent="minimal"
                      ref={value === '' ? selectedOptionRef : null}
                      className={cn(
                        'w-full justify-start text-clip p-2 font-normal text-default hover:bg-subtle hover:text-emphasis',
                        { 'bg-subtle': currentValue === '' }
                      )}
                      onKeyDown={handleKeyDown}
                      data-testid={`multiple-choice-menu-options-empty-${fieldId}-${rowId}`}
                      onClick={() => {
                        setIsEditing(false);
                        void onSelection('');
                      }}
                    >
                      {currentField?.format?.default === 'kn-blank'
                        ? currentField?.format?.blank
                        : ''}
                    </Button>
                  </div>
                )}

                {filteredOptions?.map((option) => (
                  <div
                    className={cn(
                      'mt-0.5 flex w-full items-center rounded-md hover:bg-subtle hover:text-emphasis',
                      {
                        'bg-subtle': option === currentValue && !isMultiselect
                      }
                    )}
                    key={option}
                  >
                    {isMultiselect && (
                      <Checkbox
                        data-testid={`multiple-choice-field-checkbox-${option}-${fieldId}-${rowId}`}
                        checked={currentRawValue.includes(option)}
                        onCheckedChange={() => onMultiSelection(option)}
                        className="mr-2"
                      />
                    )}

                    <Button
                      intent="minimal"
                      ref={option === currentValue ? selectedOptionRef : null}
                      className="w-full justify-start truncate p-2 font-normal text-default"
                      onKeyDown={handleKeyDown}
                      data-testid={`multiple-choice-menu-options-${option}-${fieldId}-${rowId}`}
                      onClick={() => {
                        if (isMultiselect) {
                          void onMultiSelection(option);
                        } else {
                          setIsEditing(false);
                          void onSelection(option);
                        }
                      }}
                    >
                      {option}
                    </Button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <CellErrors rowId={rowId} fieldId={fieldId} testIdPrefix="multiple-choice-edit-error" />

      <div
        className={cn('flex h-6 items-center rounded bg-default', {
          'bg-emphasis':
            selectedCell.isEditing && (isMultiselect ? currentRawValue.length === 0 : true)
        })}
      >
        <ChevronDownIcon
          className="mx-0.5"
          size={16}
          onClick={(e) => {
            if (!selectedCell.isEditing) {
              setIsEditing(true);
              setCursorPositionAtTheEnd(e);
            } else {
              setIsEditing(false);
            }
          }}
        />
      </div>
    </div>
  );
}
